/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ActionGroup, ActionType } from "client-code/actions";
import type {
  TAction,
  TActionsParams,
  TCustomActionList,
  TPreviewServiceGetOfferPreviewParamsV4
} from "@tivocorp/data-domain-lib";

import { PlaybackError, GenericErrorCode } from "../error";
import { MetadataType } from "../enums";
import {
  IMetadata,
  IMetadataModel,
  ISocuMetadataParams,
  TMetadataModelParams
} from "../interfaces";
import {
  ActionsInteractor,
  PreviewInteractor,
  VodInteractor
} from "../../interactor";

export class SocuMetadataModelImpl implements IMetadataModel {
  private static TAG: string = "SocuMetadataModelImpl";

  private static MISSING_OFFER_ID_MESSAGE: string =
    "Missing offerId for SOCU metadata";

  private static ACTIONS_UNAVAILABLE_TITLE: string = "Actions Unavailable";

  private static NO_WATCH_ACTION_DESCRIPTION: string =
    "Unable to retrieve a watch action for this program.";

  private static INVALID_PARAMS_TITLE: string = "Invalid Parameters";

  private static INVALID_PARAMS_DESCRIPTION: string =
    "The metadata parameters are not valid for this content.";

  private static DEFAULT_ACTION_GROUPS: ActionGroup[] = [
    ActionGroup.Watch,
    ActionGroup.ScheduleRecording,
    ActionGroup.ScheduleBookmark,
    ActionGroup.ScheduleOnepass,
    ActionGroup.Rent
  ];

  private mMetadata: IMetadata = {};

  private mError: PlaybackError = new PlaybackError("", "");

  private mPreviewInteractor: PreviewInteractor = new PreviewInteractor();

  private mActionsInteractor: ActionsInteractor = new ActionsInteractor();

  private async fetchVodOfferPreviewData(socuOfferId: string): Promise<void> {
    const previewParams: TPreviewServiceGetOfferPreviewParamsV4 = {
      offerId: socuOfferId
    };
    const offerPreview = await this.mPreviewInteractor.fetchOfferPreviewV4(
      previewParams,
      false
    );
    this.mMetadata.offerPreview = offerPreview as unknown as Record<
      string,
      unknown
    >;
  }

  private getSocuWatchActionFromActions(
    actions: Array<TAction>,
    actionType: string
  ): TAction {
    const watchAction = actions.find((action: TAction) => {
      return action.actionType === actionType;
    });

    if (watchAction === undefined) {
      throw new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "No SOCU watch action available",
        SocuMetadataModelImpl.ACTIONS_UNAVAILABLE_TITLE,
        SocuMetadataModelImpl.NO_WATCH_ACTION_DESCRIPTION
      );
    }

    return watchAction;
  }

  private getSocuFlagsFromActions(actions: Array<TAction>): {
    isCatchUp: boolean;
    isStartOver: boolean;
  } {
    const watchAction = actions.find((action: TAction) => {
      return action.actionType === ActionType.WatchFromProviderAction;
    });

    if (watchAction === undefined || watchAction === null) {
      return {
        isCatchUp: false,
        isStartOver: false
      };
    }

    const actionRecord = watchAction as unknown as Record<string, unknown>;
    if (
      !("kernel" in actionRecord) ||
      actionRecord.kernel === undefined ||
      actionRecord.kernel === null
    ) {
      return {
        isCatchUp: false,
        isStartOver: false
      };
    }

    const kernelRecord = actionRecord.kernel as Record<string, unknown>;
    return {
      isCatchUp:
        "isCatchUp" in kernelRecord &&
        typeof kernelRecord.isCatchUp === "boolean"
          ? kernelRecord.isCatchUp
          : false,
      isStartOver:
        "isStartOver" in kernelRecord &&
        typeof kernelRecord.isStartOver === "boolean"
          ? kernelRecord.isStartOver
          : false
    };
  }

  private getSocuOfferIdFromAction(action: TAction): string | undefined {
    const actionRecord = action as unknown as Record<string, unknown>;
    if (
      !("kernel" in actionRecord) ||
      actionRecord.kernel === undefined ||
      actionRecord.kernel === null ||
      typeof actionRecord.kernel !== "object"
    ) {
      return undefined;
    }

    const kernelRecord = actionRecord.kernel as Record<string, unknown>;
    if (
      "socuOfferId" in kernelRecord &&
      typeof kernelRecord.socuOfferId === "string" &&
      kernelRecord.socuOfferId !== ""
    ) {
      return kernelRecord.socuOfferId;
    }

    return undefined;
  }

  private async retrieveActionList(params: ISocuMetadataParams): Promise<void> {
    const offerId = params.offerId || "";
    const actionsParams: TActionsParams = {
      offerId,
      groups: SocuMetadataModelImpl.DEFAULT_ACTION_GROUPS
    };

    const actionListResponse: TCustomActionList =
      await this.mActionsInteractor.fetchOfferActions(actionsParams, false, {
        bypassNavigationCancellation: true
      });

    if (
      actionListResponse.actions === undefined ||
      actionListResponse.actions === null ||
      actionListResponse.actions.length === 0
    ) {
      throw new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "No actions available for SOCU action list",
        SocuMetadataModelImpl.ACTIONS_UNAVAILABLE_TITLE,
        "Unable to retrieve available actions for this program."
      );
    }

    this.mMetadata.actions = actionListResponse.actions;
  }

  private async retrieveActionDetails(): Promise<void> {
    const actions = this.mMetadata.actions;
    const socuFlags =
      actions === undefined || actions === null
        ? { isCatchUp: false, isStartOver: false }
        : this.getSocuFlagsFromActions(actions);

    const watchFromProviderActions = this.getSocuWatchActionFromActions(
      actions === undefined || actions === null
        ? ([] as Array<TAction>)
        : actions,
      ActionType.WatchFromProviderAction
    );
    const offerId = this.getSocuOfferIdFromAction(watchFromProviderActions);

    // Always fetched fresh: the kernel carries the saved position, which
    // changes every time the airing is watched. The action-details cache lives
    // for the whole app session, so a cached response would resume a replay
    // from wherever the first launch found it rather than where the viewer
    // left off. Kaveri fetches this on every launch for the same reason.
    const actionDetailsResponse =
      await new VodInteractor().fetchOttOfferActionDetails(
        {
          offerId: offerId || "",
          isCatchUp: socuFlags.isCatchUp,
          isStartOver: socuFlags.isStartOver
        },
        true
      );

    if (
      actionDetailsResponse.watchActionsList === undefined ||
      actionDetailsResponse.watchActionsList === null ||
      actionDetailsResponse.watchActionsList.length === 0
    ) {
      throw new PlaybackError(
        GenericErrorCode.UNKNOWN_ERROR,
        "No SOCU watch action available",
        SocuMetadataModelImpl.ACTIONS_UNAVAILABLE_TITLE,
        SocuMetadataModelImpl.NO_WATCH_ACTION_DESCRIPTION
      );
    }

    const watchAction = this.getSocuWatchActionFromActions(
      actionDetailsResponse.watchActionsList as unknown as Array<TAction>,
      "watchFromSoCuAction"
    );
    this.mMetadata.watchAction =
      watchAction as unknown as IMetadata["watchAction"];
  }

  // TODO: if screenType param is not differing the standard and full preview response then merge the retrieveSocuFullPreview and fetchVodOfferPreviewData function
  private async retrieveSocuFullPreview(
    params: ISocuMetadataParams
  ): Promise<void> {
    const offerId = params.offerId || "";
    const { offerPreview } = this.mMetadata;

    if (!offerPreview) {
      return Promise.reject();
    }
    return this.fetchVodOfferPreviewData(offerId);
  }

  private async retrieveSocuPreview(
    params: ISocuMetadataParams
  ): Promise<void> {
    const offerId = params.offerId || "";
    return this.fetchVodOfferPreviewData(offerId);
  }

  private isSocuParams(
    params: TMetadataModelParams
  ): params is ISocuMetadataParams {
    return (
      params.metadataType === MetadataType.ACTION_LIST ||
      params.metadataType === MetadataType.ACTION_DETAILS ||
      params.metadataType === MetadataType.PREVIEW ||
      params.metadataType === MetadataType.FULL_INFO_PREVIEW ||
      params.metadataType === MetadataType.VOD_ACTION_LIST
    );
  }

  public getMetadata(): IMetadata {
    return this.mMetadata;
  }

  public getError(): PlaybackError {
    return this.mError;
  }

  /** Normalizes any thrown value into a PlaybackError carrying V555. */
  private toPlaybackError(error: unknown): PlaybackError {
    if (error instanceof PlaybackError) {
      return error;
    }
    const message = error instanceof Error ? error.message : String(error);
    return new PlaybackError(
      GenericErrorCode.UNKNOWN_ERROR,
      message,
      "Content Unavailable",
      "Unable to retrieve information for this program."
    );
  }

  public prepareMetadataByType(
    playbackType: TMetadataModelParams
  ): Promise<void> {
    if (!this.isSocuParams(playbackType)) {
      return Promise.resolve();
    }

    const { metadataType } = playbackType;
    if (metadataType === MetadataType.ACTION_LIST) {
      if (!playbackType.offerId) {
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          SocuMetadataModelImpl.MISSING_OFFER_ID_MESSAGE,
          SocuMetadataModelImpl.INVALID_PARAMS_TITLE,
          SocuMetadataModelImpl.INVALID_PARAMS_DESCRIPTION
        );
        throw this.mError;
      }
      return this.retrieveActionList(playbackType).catch((error: unknown) => {
        this.mError = this.toPlaybackError(error);
        throw this.mError;
      });
    } else if (metadataType === MetadataType.ACTION_DETAILS) {
      return this.retrieveActionDetails().catch((error: unknown) => {
        this.mError = this.toPlaybackError(error);
        throw this.mError;
      });
    } else if (metadataType === MetadataType.PREVIEW) {
      if (!playbackType.offerId) {
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          SocuMetadataModelImpl.MISSING_OFFER_ID_MESSAGE,
          SocuMetadataModelImpl.INVALID_PARAMS_TITLE,
          SocuMetadataModelImpl.INVALID_PARAMS_DESCRIPTION
        );
        throw this.mError;
      }
      return this.retrieveSocuPreview(playbackType).catch((error: unknown) => {
        this.mError = this.toPlaybackError(error);
        throw this.mError;
      });
    } else if (metadataType === MetadataType.FULL_INFO_PREVIEW) {
      if (!playbackType.offerId) {
        this.mError = new PlaybackError(
          GenericErrorCode.UNKNOWN_ERROR,
          SocuMetadataModelImpl.MISSING_OFFER_ID_MESSAGE,
          SocuMetadataModelImpl.INVALID_PARAMS_TITLE,
          SocuMetadataModelImpl.INVALID_PARAMS_DESCRIPTION
        );
        throw this.mError;
      }
      return this.retrieveSocuFullPreview(playbackType).catch(
        (error: unknown) => {
          this.mError = this.toPlaybackError(error);
          throw this.mError;
        }
      );
    } else {
      return Promise.resolve();
    }
  }
}
